import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronRight, ArrowUpRight, Clock } from 'lucide-react';
import { AnnouncementBar } from '../components/AnnouncementBar';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { ARTICLES, type ArticleCategory } from '../components/articles';
const CATEGORIES: ArticleCategory[] = ['Protocols', 'Science', 'Guides', 'Research notes'];
function formatDate(iso: string) {
  const d = new Date(iso);
  return d.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
}
export function Articles() {
  const [active, setActive] = useState<ArticleCategory | 'All'>('All');
  const filtered = useMemo(() => {
    if (active === 'All') return ARTICLES;
    return ARTICLES.filter(a => a.category === active);
  }, [active]);
  const featured = ARTICLES[0];
  const rest = filtered.filter(a => a.id !== featured.id);
  return <div className="min-h-screen w-full bg-background" data-id="element-992">
      <AnnouncementBar data-id="element-993" />
      <Header data-id="element-994" />

      <main data-id="element-995">
        {/* Page header */}
        <section className="w-full border-b border-border" data-id="element-996">
          <div className="container mx-auto pt-10 lg:pt-14 pb-10" data-id="element-997">
            <nav className="flex items-center gap-1.5 text-[12px] text-muted-foreground mb-6" data-id="element-998">
              <Link to="/" className="hover:text-foreground transition-colors" data-id="element-999">
                Home
              </Link>
              <ChevronRight className="h-3.5 w-3.5" strokeWidth={1.5} data-id="element-1000" />
              <span className="text-foreground" data-id="element-1001">Journal</span>
            </nav>

            <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.45
          }} className="max-w-3xl" data-id="element-1002">
              <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium mb-3" data-id="element-1003">
                Research notes & protocols
              </p>
              <h1 className="text-4xl lg:text-6xl font-semibold tracking-[-0.025em] text-foreground leading-[1.05]" data-id="element-1004">
                Peptide research,
                <br data-id="element-1005" />
                without the marketing
                <span className="font-mono text-primary" data-id="element-1006">.</span>
              </h1>
              <p className="mt-5 text-[15px] text-muted-foreground max-w-xl leading-relaxed" data-id="element-1007">
                Protocol breakdowns, COA walkthroughs, and in-vitro research
                summaries. Written by our lab team, for researchers. No claims,
                no fluff.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Category pills */}
        <section className="w-full border-b border-border bg-card" data-id="element-1008">
          <div className="container mx-auto py-3.5" data-id="element-1009">
            <div className="flex items-center gap-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-id="element-1010">
              <button onClick={() => setActive('All')} className={`inline-flex items-center gap-2 px-3.5 h-9 rounded-full text-[12.5px] font-medium whitespace-nowrap transition-colors ${active === 'All' ? 'bg-foreground text-background' : 'text-muted-foreground bg-background border border-border hover:text-foreground hover:border-foreground/30'}`} data-id="element-1011">
                All articles
                <span className={`font-mono text-[11px] [font-feature-settings:'tnum'] ${active === 'All' ? 'text-background/60' : 'text-muted-foreground/70'}`} data-id="element-1012">
                  {ARTICLES.length}
                </span>
              </button>
              {CATEGORIES.map(c => {
              const count = ARTICLES.filter(a => a.category === c).length;
              const isActive = active === c;
              return <button key={c} onClick={() => setActive(c)} className={`inline-flex items-center gap-2 px-3.5 h-9 rounded-full text-[12.5px] font-medium whitespace-nowrap transition-colors ${isActive ? 'bg-foreground text-background' : 'text-muted-foreground bg-background border border-border hover:text-foreground hover:border-foreground/30'}`} data-id="element-1013">
                    {c}
                    <span className={`font-mono text-[11px] [font-feature-settings:'tnum'] ${isActive ? 'text-background/60' : 'text-muted-foreground/70'}`} data-id="element-1014">
                      {count}
                    </span>
                  </button>;
            })}
            </div>
          </div>
        </section>

        {/* Articles grid */}
        <section className="container mx-auto py-12 lg:py-16" data-id="element-1015">
          {/* Featured (only when on All view) */}
          {active === 'All' && <Link to={`/articles/${featured.slug}`} className="block group mb-12" data-id="element-1016">
              <article className="grid lg:grid-cols-[1.2fr_1fr] gap-8 lg:gap-12 items-center rounded-3xl border border-border bg-card p-8 lg:p-12 hover:border-foreground/20 transition-colors" data-id="element-1017">
                <div data-id="element-1018">
                  <div className="flex items-center gap-3 mb-5" data-id="element-1019">
                    <span className="text-[10px] uppercase tracking-wider font-semibold text-primary px-2 py-1 rounded-full bg-primary/10" data-id="element-1020">
                      Featured · {featured.category}
                    </span>
                    <span className="font-mono text-[11px] text-muted-foreground" data-id="element-1021">
                      {featured.readMinutes} min read
                    </span>
                  </div>
                  <h2 className="text-3xl lg:text-[40px] font-semibold tracking-[-0.025em] text-foreground leading-[1.1]" data-id="element-1022">
                    {featured.title}
                  </h2>
                  <p className="mt-4 text-[15px] text-muted-foreground leading-relaxed max-w-xl" data-id="element-1023">
                    {featured.excerpt}
                  </p>
                  <div className="mt-6 flex items-center gap-3 text-[12px]" data-id="element-1024">
                    <span className="text-foreground font-medium" data-id="element-1025">
                      {featured.author}
                    </span>
                    <span className="text-border" data-id="element-1026">·</span>
                    <span className="text-muted-foreground" data-id="element-1027">
                      {featured.authorRole}
                    </span>
                    <span className="text-border" data-id="element-1028">·</span>
                    <span className="font-mono text-muted-foreground" data-id="element-1029">
                      {formatDate(featured.publishedAt)}
                    </span>
                  </div>
                  <span className="mt-7 inline-flex items-center gap-1.5 text-[13.5px] font-semibold text-foreground group-hover:text-primary transition-colors" data-id="element-1030">
                    Read article
                    <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2} data-id="element-1031" />
                  </span>
                </div>
                {/* Visual placeholder — abstract editorial card */}
                <div className="relative aspect-[5/4] rounded-2xl bg-secondary/60 border border-border overflow-hidden flex items-center justify-center" data-id="element-1032">
                  <div className="text-[120px] lg:text-[180px] font-semibold text-foreground/[0.06] tracking-[-0.05em] leading-none select-none" data-id="element-1033">
                    {String(featured.readMinutes).padStart(2, '0')}
                  </div>
                  <div className="absolute bottom-5 left-5 right-5 flex items-center justify-between" data-id="element-1034">
                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium" data-id="element-1035">
                      Minutes
                    </span>
                    <span className="font-mono text-[10px] text-muted-foreground" data-id="element-1036">
                      {featured.goals.join(' · ') || 'General'}
                    </span>
                  </div>
                </div>
              </article>
            </Link>}

          {/* Grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6" data-id="element-1037">
            {(active === 'All' ? rest : filtered).map(article => <Link key={article.id} to={`/articles/${article.slug}`} className="group block" data-id="element-1038">
                <article className="h-full flex flex-col rounded-2xl border border-border bg-card overflow-hidden hover:border-foreground/20 transition-colors" data-id="element-1039">
                  <div className="relative aspect-[5/3] bg-secondary/50 border-b border-border flex items-center justify-center overflow-hidden" data-id="element-1040">
                    <div className="text-[80px] font-semibold text-foreground/[0.07] tracking-[-0.05em] leading-none select-none" data-id="element-1041">
                      {String(article.readMinutes).padStart(2, '0')}
                    </div>
                    <span className="absolute top-3.5 left-3.5 text-[10px] uppercase tracking-wider font-medium text-foreground px-2 py-1 rounded-full bg-background border border-border" data-id="element-1042">
                      {article.category}
                    </span>
                  </div>
                  <div className="flex-1 p-5 flex flex-col" data-id="element-1043">
                    <h3 className="text-[17px] font-semibold tracking-[-0.015em] text-foreground leading-tight group-hover:text-primary transition-colors" data-id="element-1044">
                      {article.title}
                    </h3>
                    <p className="mt-2 text-[13px] text-muted-foreground line-clamp-3 leading-relaxed" data-id="element-1045">
                      {article.excerpt}
                    </p>
                    <div className="mt-auto pt-4 flex items-center justify-between text-[11px]" data-id="element-1046">
                      <span className="text-muted-foreground" data-id="element-1047">
                        <span className="text-foreground font-medium" data-id="element-1048">
                          {article.author}
                        </span>
                      </span>
                      <span className="inline-flex items-center gap-1 font-mono text-muted-foreground [font-feature-settings:'tnum']" data-id="element-1049">
                        <Clock className="h-3 w-3" strokeWidth={1.75} data-id="element-1050" />
                        {article.readMinutes} min
                      </span>
                    </div>
                  </div>
                </article>
              </Link>)}
          </div>
        </section>
      </main>

      <Footer data-id="element-1051" />
    </div>;
}